import { type BookmarkEntityType, MyBookmarkedEntityIdsDocument } from '@mdvip/shared/graphql/generated/graphql';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

/** Prefix for every cached saved-id check of one entity type, whatever ids it covered. */
export const myBookmarkedEntityIdsPrefix = (entityType: BookmarkEntityType) =>
  ['bookmarks', 'myBookmarkedEntityIds', entityType] as const;

/** Sorted, so the same screenful in a different order shares one cache entry. */
export const myBookmarkedEntityIdsKey = (entityType: BookmarkEntityType, ids: string[]) =>
  [...myBookmarkedEntityIdsPrefix(entityType), [...ids].sort()] as const;

/**
 * Which of `ids` the member has bookmarked, for content types whose list query carries no
 * `isBookmarked` field: one bulk check per screenful (the backend takes 1–100 ids) rather than one
 * per card.
 *
 * A failed check is reported to telemetry and otherwise reads as "nothing saved": the icons render
 * unsaved and the page carries on.
 */
export function useMyBookmarkedEntityIds(entityType: BookmarkEntityType, ids: string[]): Set<string> {
  const queryKey = myBookmarkedEntityIdsKey(entityType, ids);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await gqlRequest(MyBookmarkedEntityIdsDocument, {
        input: { entityType, entityIds: queryKey[3], kind: 'BOOKMARK' },
      });
      return data.myBookmarkedEntityIds;
    },
    enabled: ids.length > 0,
    // Paging swaps the id set; holding the last answer keeps the icons from blinking unsaved meanwhile.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!query.error) return;
    // Never the raw client error: it embeds the member's id and the entity ids.
    trackException(new Error(`myBookmarkedEntityIds failed: ${extractGqlErrorCode(query.error)}`), {
      source: 'myBookmarkedEntityIds',
      entityType,
    });
  }, [query.error, entityType]);

  return useMemo(() => new Set(query.data ?? []), [query.data]);
}
